<template>
    <DCard padding>
        <DCol>
            <DRow
                class="mb-3"
                align-vertical="end"
            >
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="user-search"
                        v-model="filtersData.subject"
                        :label="__('tools.communications.filters.subject')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="user-search"
                        v-model="filtersData.email"
                        :label="__('tools.communications.filters.email')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="user-search"
                        v-model="filtersData.mobileNumber"
                        :label="__('tools.communications.filters.mobile_number')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="user-search"
                        v-model="filtersData.customer"
                        :label="__('tools.communications.filters.customer')"
                        class="mb-0"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DSelect
                        id="subject-dropdown"
                        v-model="filtersData.type"
                        :label="__('tools.communications.filters.type')"
                        :options="types"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DDateFormatInput
                        id="date_from"
                        v-model="filtersData.dateFrom"
                        format="YYYY-MM-DD"
                        type="date"
                        :label="__('tools.communications.filters.date_from')"
                        @keydown.enter.exact.prevent="changePage(1)"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DDateFormatInput
                        id="date_to"
                        v-model="filtersData.dateTo"
                        format="YYYY-MM-DD"
                        :have-class="'mb-0'"
                        :label="__('tools.communications.filters.date_to')"
                        :placeholder="'Select date'"
                        class="mb-0"
                        type="date"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('tools.communications.filters.filter')"
                        @click="changePage"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('tools.communications.filters.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </DCol>
        <DTable
            :fields="[
                { key: 'subject', label: __('tools.communications.table.subject') },
                { key: 'recipients', label: __('tools.communications.table.recipients') },
                { key: 'customer', label: __('tools.communications.table.customer') },
                { key: 'status', label: __('tools.communications.table.status') },
                { key: 'created_at', label: __('tools.communications.table.created') },
                { key: 'action', label: __('tools.communications.table.action') }
            ]"
            class="tableText"
            :items="props.communications.data"
        >
            <template #customer="{item}">
                <td v-if="item.customer">
                    <Link :href="customerRoute(item.customer)">
                        {{ fullName(item.customer) }}
                    </Link>
                </td>
                <td v-else />
            </template>
            <template #recipients="{item}">
                <td>
                    {{ showRecipients(item.recipients) }}
                </td>
            </template>
            <template #created_at="{item}">
                <td>
                    {{ setDateFormat(item.created_at) }}
                </td>
            </template>
            <template #action="{item}">
                <td>
                    <div style="display: flex; grid-gap: 10px;">
                        <DButton
                            icon="eye"
                            mini
                            @click="displayModal(item)"
                        />
                    </div>
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="props.communications.last_page > 1"
            :active-page="props.communications.current_page"
            :pages="props.communications.last_page"
            align="center"
            @update:activePage="changePage"
        />
        <ShowEmailModal
            :show-modal="showEmailModal"
            :email-id="modalEmailId"
            :customer="customer"
            @closeModal="closeModal"
        />

        <ShowSmsModal
            :show-modal="showSmsModal"
            :sms-id="modalSmsId"
            :customer="customer"
            @closeModal="closeModal"
        />
    </DCard>
</template>

<script setup>
import ShowEmailModal from '~@/Components/Tools/Communications/ShowEmailModal'
import ShowSmsModal from '~@/Components/Tools/Communications/ShowSmsModal'
import { ref } from 'vue'
import { route } from '~@/Composables/route'

const props = defineProps({
    communications: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    types: {
        type: Array,
        default: () => {
            return []
        }
    }
})

const emit = defineEmits(['changePage', 'resetFilters'])

const showEmailModal = ref(false)
const showSmsModal = ref(false)
const modalEmailId = ref(0)
const modalSmsId = ref(0)
const customer = ref(null)
const filtersData = ref(props.filters)

const changePage = (page) => emit('changePage', page ?? 1, filtersData.value)
const resetFilters = (page) => emit('resetFilters', props.communications.current_page)
const customerRoute = (value) => route('customers.customers.show', value.id)
const fullName = (value) => `${value.firstname ?? ''} ${value.lastname ?? ''}`
const showRecipients = (value) => JSON.parse(value).join(', ')
const closeModal = () => {
    showEmailModal.value = false
    showSmsModal.value = false
}

const displayModal = (value) => {
    customer.value = value.customer

    if (value.type === 'email') {
        showEmailModal.value = true
        showSmsModal.value = false
        modalEmailId.value = value.id
    }

    if (value.type === 'sms') {
        showSmsModal.value = true
        showEmailModal.value = false
        modalSmsId.value = value.id
    }
}
</script>

<style lang="scss" scoped>
.button {
    background-color: rgb(26, 77, 128);
    color: white;
    font-size: 16px;
}
.tableText {
    text-align: center
}
</style>
